import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Stack,
  Fade,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { 
  buscarDocumentosServidor, 
  buscarPdfParaAssinatura,
  downloadPdfAssinado,
  assinarDocumentoComImagem,
  gerarContrato,
  searchEmployeeByCPF 
} from '../../services/api';
import toast from 'react-hot-toast';
import DocumentAuthSimple from '../../components/DocumentAuthSimple/DocumentAuthSimple';

interface Document {
  id: number;
  nome_arquivo: string;
  tipo: string;
  status: string;
  data_assinatura: string;
  created_at: string;
  tamanho: number;
  hash_assinatura?: string;
  tem_assinatura?: boolean;
}

interface ContractFormData {
  estado_civil: string;
  matricula: string;
  identidade: string;
  endereco: string;
  endereco_numero: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  banco: string;
  agencia: string;
  conta: string;
  tipo_conta: 'corrente' | 'poupanca';
  pix: string;
  telefone: string; 
}

interface Servidor {
  id: number;
  nome_completo: string;
  cpf: string;
  email: string;
  telefone?: string;
  estado_civil?: string;
  matricula?: string;
  identidade?: string;
  endereco?: string;
  endereco_numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  banco?: string;
  agencia?: string;
  conta?: string;
  tipo_conta?: 'corrente' | 'poupanca';
  pix?: string;
  created_at: string;
  updated_at: string;
}

const DocumentPage: React.FC = () => {
  const { servidorId } = useParams<{ servidorId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfLoadProgress, setPdfLoadProgress] = useState(0);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [servidorData, setServidorData] = useState<Servidor | null>(null);
  
  // Estados para assinatura
  const [openSignModal, setOpenSignModal] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [manualSignature, setManualSignature] = useState('');
  const [assinaturaTipo, setAssinaturaTipo] = useState<'digital' | 'upload'>('digital');
  const [assinaturaConcluida, setAssinaturaConcluida] = useState(false);

  // Estados para o formulário de contrato
  const [showContractForm, setShowContractForm] = useState(false);
  const [contractFormData, setContractFormData] = useState<ContractFormData>({
    estado_civil: '',
    matricula: '',
    identidade: '',
    endereco: '',
    endereco_numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    pix: '',
    telefone: servidorData?.telefone || '',
  });
  const [submittingContract, setSubmittingContract] = useState(false);

  useEffect(() => {
    if (servidorId) {
      loadData();
    } else {
      setShowAuthForm(true);
      setLoading(false);
    }
  }, [servidorId]);

  const loadData = async () => {
      if (!servidorId) {
        navigate('/documentos');
        return;
      }

      setLoading(true);
      try {
        // 1. Buscar documentos do servidor
        const docs: Document[] = await buscarDocumentosServidor(parseInt(servidorId));
        setDocuments(docs);

        // 2. Buscar dados atualizados do servidor usando o CPF armazenado
        const storedData = localStorage.getItem('servidorData');
        if (storedData) {
          const parsed = JSON.parse(storedData) as Servidor;
          // Se temos o CPF, buscamos os dados mais recentes
          if (parsed.cpf) {
            try {
              const response = await searchEmployeeByCPF(parsed.cpf);
              if (response?.servidor) {
                const servidorAtualizado = response.servidor as Servidor;
                setServidorData(servidorAtualizado);
                // Atualiza o localStorage com os dados frescos
                localStorage.setItem('servidorData', JSON.stringify(servidorAtualizado));

                // Preenche o formulário de contrato com os dados atualizados
                setContractFormData(prev => ({
                  ...prev,
                  estado_civil: servidorAtualizado.estado_civil || '',
                  telefone: servidorAtualizado.telefone || '',
                  matricula: servidorAtualizado.matricula || '',
                  identidade: servidorAtualizado.identidade || '',
                  endereco: servidorAtualizado.endereco || '',
                  endereco_numero: servidorAtualizado.endereco_numero || '',
                  bairro: servidorAtualizado.bairro || '',
                  cidade: servidorAtualizado.cidade || '',
                  estado: servidorAtualizado.estado || '',
                  cep: servidorAtualizado.cep || '',
                  banco: servidorAtualizado.banco || '',
                  agencia: servidorAtualizado.agencia || '',
                  conta: servidorAtualizado.conta || '',
                  tipo_conta: servidorAtualizado.tipo_conta || 'corrente',
                  pix: servidorAtualizado.pix || ''
                }));
              } else {
                // Se não conseguir buscar, usa os dados do localStorage como fallback
                setServidorData(parsed);
              }
            } catch (error) {
              console.error('Erro ao buscar dados atualizados do servidor:', error);
              // Fallback para dados do localStorage
              setServidorData(parsed);
            }
          } else {
            // Se não tem CPF no storedData, usa os dados como estão
            setServidorData(parsed);
          }
        } else {
          // Se não há dados no localStorage, mostra formulário de autenticação
          setShowAuthForm(true);
          return;
        }

        // 3. Verificar se já existe um contrato
        const contratoExistente = docs.find((doc: Document) => doc.tipo === 'CONTRATO');
        if (!contratoExistente) {
          setShowContractForm(true);
          setSelectedDocument(null);
        } else {
          setShowContractForm(false);
          if (docs.length > 0) {
            const doc = docs[0];
            setSelectedDocument(doc);
            await handleViewPdf(doc.id);
          }
        }
      } catch (error: any) {
        console.error('Erro ao carregar documentos:', error);
        toast.error(error.response?.data?.message || 'Erro ao carregar documentos');
        setShowAuthForm(true);
      } finally {
        setLoading(false);
      }
    };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleViewPdf = async (documentId: number) => {
    if (!servidorId) return;
    
    setLoadingPdf(true);
    setPdfLoadProgress(0);
    
    const progressInterval = setInterval(() => {
      setPdfLoadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      toast.loading('Carregando documento...', { id: 'pdf-load' });
      const response = await buscarPdfParaAssinatura(
        documentId, 
        parseInt(servidorId)
      );
      
      setPdfData(response.pdf_base64);
      setPdfLoadProgress(100);
      
      toast.success('Documento carregado!', { id: 'pdf-load' });
      
      const updatedDoc = documents.find(doc => doc.id === documentId);
      if (updatedDoc) {
        setSelectedDocument(updatedDoc);
      }
      
    } catch (error: any) {
      console.error('Erro ao carregar PDF:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar PDF', { id: 'pdf-load' });
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => setPdfLoadProgress(0), 500);
      setLoadingPdf(false);
    }
  };

  const handleSelectDocument = async (doc: Document) => {
    setSelectedDocument(doc);
    setAssinaturaConcluida(false);
    await handleViewPdf(doc.id);
  };

  const handleOpenSignModal = () => {
    setOpenSignModal(true);
    setSignatureImage(null);
    setManualSignature('');
    setAssinaturaTipo('digital');
  };

  const handleCloseSignModal = () => {
    setOpenSignModal(false);
  };

  const handleSignatureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.match('image.*')) {
        toast.error('Por favor, selecione um arquivo de imagem (PNG, JPG)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setSignatureImage(result);
        toast.success('Assinatura carregada com sucesso!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveSignature = () => {
    setSignatureImage(null);
    toast.success('Assinatura removida');
  };

  const handleManualSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManualSignature(e.target.value);
  };

  const handleCreateSignatureFromText = () => {
    if (!manualSignature.trim()) {
      toast.error('Digite sua assinatura');
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      toast.error('Erro ao criar assinatura');
      return;
    }

    canvas.width = 600;
    canvas.height = 180;

    ctx.fillStyle = 'transparent';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.font = 'italic 40px "Brush Script MT", cursive';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(manualSignature, canvas.width / 2, canvas.height / 2);
    
    const dataUrl = canvas.toDataURL('image/png');
    setSignatureImage(dataUrl);
    toast.success('Assinatura criada com sucesso!');
  };

  const handleSignDocument = async () => {
    if (!selectedDocument || !servidorData || !servidorId) return;
    if (!signatureImage) {
      toast.error('Por favor, carregue ou crie sua assinatura');
      return;
    }

    setIsSigning(true);
    try {
      toast.loading('Assinando documento...', { 
        id: 'sign',
        duration: 10000 
      });
      
      await assinarDocumentoComImagem(
        parseInt(servidorId),
        selectedDocument.id,
        signatureImage,
        servidorData.cpf
      );
      
      const updatedSelectedDoc = {
        ...selectedDocument,
        status: 'ASSINADO',
        tem_assinatura: true,
        data_assinatura: new Date().toISOString()
      };
      setSelectedDocument(updatedSelectedDoc);
      
      const updatedDocuments = documents.map(doc => 
        doc.id === selectedDocument.id 
          ? updatedSelectedDoc 
          : doc
      );
      setDocuments(updatedDocuments);
      
      setLoadingPdf(true);
      try {
        const response = await buscarPdfParaAssinatura(
          selectedDocument.id, 
          parseInt(servidorId)
        );
        setPdfData(response.pdf_base64);
      } catch (error) {
        console.error('Erro ao recarregar PDF assinado:', error);
      }
      setLoadingPdf(false);
      
      setAssinaturaConcluida(true);
      toast.success('Documento assinado com sucesso!', { 
        id: 'sign',
        duration: 5000 
      });
      
      handleCloseSignModal();
      
    } catch (error: any) {
      console.error('Erro ao assinar documento:', error);
      toast.error(error.response?.data?.message || 'Erro ao assinar documento', { 
        id: 'sign',
        duration: 5000 
      });
    } finally {
      setIsSigning(false);
    }
  };

  const handleDownloadSignedPdf = async () => {
    if (!selectedDocument || !selectedDocument.tem_assinatura) {
      toast.error('Documento não está assinado');
      return;
    }

    try {
      toast.loading('Baixando documento assinado...', { id: 'download' });
      
      const blob = await downloadPdfAssinado(
        parseInt(servidorId!),
        selectedDocument.id
      );
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedDocument.nome_arquivo.replace('.pdf', '_assinado.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download iniciado!', { id: 'download' });
    } catch (error: any) {
      console.error('Erro ao baixar PDF assinado:', error);
      toast.error(error.response?.data?.message || 'Erro ao baixar documento assinado', { id: 'download' });
    }
  };

  const handleDownloadPdf = () => {
    if (!pdfData) {
      toast.error('Nenhum documento disponível para download');
      return;
    }
    
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${pdfData}`;
    link.download = selectedDocument?.nome_arquivo || 'documento.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ASSINADO':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'EXPIRADO':
        return 'error';
      default:
        return 'default';
    }
  };

  // Handlers para o formulário de contrato
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContractFormData({
      ...contractFormData,
      [e.target.name]: e.target.value
    });
  };

  const fetchAddressByCep = async (cep: string) => {
    if (cep.length !== 9) return; // formato 00000-000
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`);
      if (!response.data.erro) {
        setContractFormData(prev => ({
          ...prev,
          endereco: response.data.logradouro,
          bairro: response.data.bairro,
          cidade: response.data.localidade,
          estado: response.data.uf,
        }));
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    }
  };
  const applyCepMask = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0,5)}-${numbers.slice(5,8)}`;
  };

  // Handler específico para o campo CEP (aplica máscara)
  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCepMask(e.target.value);
    setContractFormData(prev => ({ ...prev, cep: masked }));
  };

  const handleSubmitContractForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!servidorId) return;

    setSubmittingContract(true);
    try {
      toast.loading('Gerando contrato...', { id: 'gerar-contrato' });
      const novoDocumento = await gerarContrato(parseInt(servidorId), contractFormData);
      toast.success('Contrato gerado com sucesso!', { id: 'gerar-contrato' });

      // --- INÍCIO DA ATUALIZAÇÃO DOS DADOS DO SERVIDOR ---
      // Verifica se a resposta da API já contém os dados atualizados do servidor
      if (novoDocumento.servidor) {
        // Se a API retornar o servidor atualizado, use-o
        setServidorData(novoDocumento.servidor);
        localStorage.setItem('servidorData', JSON.stringify(novoDocumento.servidor));
      } else {
        // Caso contrário, busque novamente os dados do servidor usando o CPF
        // (supondo que você tenha o CPF disponível em servidorData)
        const cpfServidor = servidorData?.cpf; // ou você pode armazenar o CPF em outro lugar
        if (cpfServidor) {
          const updated = await searchEmployeeByCPF(cpfServidor);
          if (updated?.servidor) {
            setServidorData(updated.servidor);
            localStorage.setItem('servidorData', JSON.stringify(updated.servidor));
          }
        }
      }
      // --- FIM DA ATUALIZAÇÃO ---

      // Recarregar documentos
      const docs = await buscarDocumentosServidor(parseInt(servidorId));
      setDocuments(docs);

      // Selecionar o novo contrato
      const contrato = docs.find((doc: Document) => doc.id === novoDocumento.id);
      if (contrato) {
        setSelectedDocument(contrato);
        await handleViewPdf(contrato.id);
      }

      setShowContractForm(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao gerar contrato', { id: 'gerar-contrato' });
    } finally {
      setSubmittingContract(false);
    }
  };

  const SignaturePreviewBox = ({ src }: { src: string }) => (
    <Box
      sx={{
        width: '100%',
        maxWidth: 420,
        height: 120,
        border: '2px solid',
        borderColor: 'grey.400',
        borderRadius: 2,
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        p: 1.5,
        boxShadow: 2,
      }}
    >
      <Box
        component="img"
        src={src}
        alt="Assinatura"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />
    </Box>
  );

  if (showAuthForm) {
    return <DocumentAuthSimple />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
          <Typography variant="h6" color="text.secondary">
            Carregando seus documentos...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <Box>
        <Box sx={{ mb: 4, textAlign: 'center', position: 'relative' }}>
          <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
            <Tooltip title="Atualizar documentos">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ color: 'primary.main' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <DescriptionIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main',
              mb: 2 
            }} 
          />
          <Typography variant="h4" color="primary.main" gutterBottom fontWeight="bold">
            {documents.some(doc => doc.status === 'PENDENTE') ? 'Meus Documentos' : 'Meus Documentos Assinados'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {documents.some(doc => doc.status === 'PENDENTE') 
              ? 'Visualize, assine e faça download dos seus documentos' 
              : 'Visualize e faça download dos seus documentos assinados'}
          </Typography>
        </Box>

        {servidorData && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {servidorData.nome_completo}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={`CPF: ${servidorData.cpf}`}
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  <Chip
                    label={`Email: ${servidorData.email || 'não informado'}`}
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  <Chip
                      label={`Telefone: ${servidorData.telefone}`}
                      size="small"
                      variant="outlined"
                      sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  <Chip
                    label={`${documents.length} documento(s)`}
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: 'white', color: 'primary.main' }}
                  />
                  {documents.some(doc => doc.status === 'PENDENTE') && (
                    <Chip
                      label="Há documentos pendentes"
                      size="small"
                      color="warning"
                      sx={{ bgcolor: 'white' }}
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    localStorage.removeItem('doc_session_token');
                    localStorage.removeItem('servidorData');
                    navigate('/documentos');
                  }}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Sair
                </Button>
              </Grid>
            </Grid>
          </Paper>
)}

        {showContractForm ? (
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom color="primary.main" sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ mr: 1 }} />
              Preencha seus dados para gerar o contrato
            </Typography>
            <Typography variant="body2" paragraph color="text.secondary">
              Precisamos de algumas informações adicionais para gerar seu contrato de procuração e termo de anuência.
              Todos os campos são obrigatórios, exceto os dados bancários e PIX.
            </Typography>

            {/* Exibir telefone do servidor (já salvo) */}
            {servidorData?.telefone && (
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Telefone cadastrado:</strong> {servidorData.telefone}
                </Typography>
              </Alert>
            )}

            <form onSubmit={handleSubmitContractForm}>
              <Grid container spacing={2}>
                {/* Dados pessoais */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mt: 2 }}>Dados Pessoais</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Estado Civil"
                    name="estado_civil"
                    value={contractFormData.estado_civil}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Matrícula"
                    name="matricula"
                    value={contractFormData.matricula}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Identidade (RG)"
                    name="identidade"
                    value={contractFormData.identidade}
                    onChange={handleInputChange}
                    required
                    placeholder="Nº do RG"
                  />
                </Grid>

                {/* Campo Telefone */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    name="telefone"
                    value={contractFormData.telefone}
                    onChange={handleInputChange}
                    placeholder="(99) 99999-9999"
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>

                {/* Campo CEP (agora antes dos demais endereços) */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={contractFormData.cep}
                    onChange={handleCepChange}          // Aplica máscara
                    onBlur={() => fetchAddressByCep(contractFormData.cep)} // Busca ao sair
                    required
                    placeholder="00000-000"
                    inputProps={{ maxLength: 9 }}
                  />
                </Grid>

                {/* Campos de endereço que serão preenchidos automaticamente */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Endereço (logradouro)"
                    name="endereco"
                    value={contractFormData.endereco}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Número"
                    name="endereco_numero"
                    value={contractFormData.endereco_numero}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    name="bairro"
                    value={contractFormData.bairro}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    name="cidade"
                    value={contractFormData.cidade}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Estado (UF)"
                    name="estado"
                    value={contractFormData.estado}
                    onChange={handleInputChange}
                    required
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>

                {/* Dados bancários (inalterados) */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mt: 2 }}>Dados Bancários</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Banco"
                    name="banco"
                    value={contractFormData.banco}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Agência"
                    name="agencia"
                    value={contractFormData.agencia}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Conta"
                    name="conta"
                    value={contractFormData.conta}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Tipo de Conta</FormLabel>
                    <RadioGroup
                      row
                      name="tipo_conta"
                      value={contractFormData.tipo_conta}
                      onChange={handleInputChange}
                    >
                      <FormControlLabel value="corrente" control={<Radio />} label="Corrente" />
                      <FormControlLabel value="poupanca" control={<Radio />} label="Poupança" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="PIX (se preferir)"
                    name="pix"
                    value={contractFormData.pix}
                    onChange={handleInputChange}
                    placeholder="CPF, e-mail ou chave aleatória"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submittingContract}
                    sx={{ mt: 2 }}
                    fullWidth
                  >
                    {submittingContract ? 'Gerando...' : 'Gerar Contrato'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        ) : (
          <Grid container spacing={4}>
            {/* Lista de Documentos */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" color="primary.main">
                    <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Documentos Disponíveis
                  </Typography>
                  <Chip 
                    label={`${documents.length} total`} 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                
                {documents.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Nenhum documento disponível para visualização.
                  </Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Documento</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc) => (
                          <TableRow 
                            key={doc.id}
                            hover
                            selected={selectedDocument?.id === doc.id}
                            onClick={() => handleSelectDocument(doc)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <DescriptionIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                                  {doc.nome_arquivo}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {formatDate(doc.created_at)} • {formatFileSize(doc.tamanho)}
                                </Typography>
                                {doc.tem_assinatura && (
                                  <Chip 
                                    label="Assinado" 
                                    size="small" 
                                    color="success"
                                    sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={doc.status} 
                                size="small"
                                color={getStatusColor(doc.status)}
                                sx={{ minWidth: 80 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>

            {/* Visualização do Documento */}
            <Grid item xs={12} md={7}>
              {selectedDocument ? (
                <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      <VisibilityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {selectedDocument.nome_arquivo}
                    </Typography>
                    <Chip 
                      label={selectedDocument.status} 
                      color={getStatusColor(selectedDocument.status)}
                    />
                  </Box>
                  
                  <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tipo do Documento
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedDocument.tipo}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tamanho
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {formatFileSize(selectedDocument.tamanho)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Criado em
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(selectedDocument.created_at)}
                          </Typography>
                        </Grid>
                        {selectedDocument.data_assinatura && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Assinado em
                            </Typography>
                            <Typography variant="body1" color="success.main">
                              {formatDate(selectedDocument.data_assinatura)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <VisibilityIcon sx={{ mr: 1, fontSize: 16 }} />
                      {selectedDocument.status === 'ASSINADO' ? 'Documento Assinado' : 'Pré-visualização do Documento'}
                    </Typography>
                    
                    {loadingPdf && (
                      <Box sx={{ mb: 2 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={pdfLoadProgress} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                          Carregando documento... {pdfLoadProgress}%
                        </Typography>
                      </Box>
                    )}
                    
                    {pdfData ? (
                      <>
                        <Box
                          sx={{
                            width: '100%',
                            height: 500,
                            border: '2px solid',
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            overflow: 'hidden',
                            mb: 2,
                            boxShadow: 2,
                          }}
                        >
                          <iframe
                            src={`data:application/pdf;base64,${pdfData}`}
                            title={selectedDocument.nome_arquivo}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                          />
                        </Box>
                        
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                          <Tooltip title="Baixar documento">
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={handleDownloadPdf}
                              sx={{ minWidth: 180 }}
                            >
                              Baixar PDF
                            </Button>
                          </Tooltip>
                          
                          {selectedDocument.status === 'ASSINADO' && selectedDocument.tem_assinatura && (
                            <Tooltip title="Baixar versão assinada">
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadSignedPdf}
                                sx={{ minWidth: 220 }}
                              >
                                Baixar PDF Assinado
                              </Button>
                            </Tooltip>
                          )}
                          
                          {selectedDocument.status === 'PENDENTE' && !selectedDocument.tem_assinatura && (
                            <Tooltip title="Assinar este documento">
                              <Button
                                variant="contained"
                                color="success"
                                onClick={handleOpenSignModal}
                                sx={{ minWidth: 200 }}
                              >
                                Assinar Documento
                              </Button>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Recarregar visualização">
                            <Button
                              variant="outlined"
                              onClick={() => handleViewPdf(selectedDocument.id)}
                              disabled={loadingPdf}
                            >
                              Recarregar
                            </Button>
                          </Tooltip>
                        </Stack>
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed', borderColor: 'grey.300', borderRadius: 2 }}>
                        {loadingPdf ? (
                          <>
                            <CircularProgress size={60} sx={{ mb: 3, color: 'primary.main' }} />
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                              Carregando documento...
                            </Typography>
                          </>
                        ) : (
                          <>
                            <DescriptionIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary" gutterBottom>
                              Documento não carregado
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              Clique em um documento da lista para visualizar
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => handleViewPdf(selectedDocument.id)}
                            >
                              Carregar Documento
                            </Button>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ) : (
                <Paper 
                  sx={{ 
                    p: 6, 
                    borderRadius: 3, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    boxShadow: 3,
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Nenhum documento selecionado
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Selecione um documento da lista ao lado para visualizar
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                  >
                    Atualizar Lista
                  </Button>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {/* Modal de Assinatura (já existente) */}
        <Dialog 
          open={openSignModal} 
          onClose={handleCloseSignModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" component="span">
                <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Assinar Documento
              </Typography>
              <IconButton onClick={handleCloseSignModal} size="small" sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent dividers sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                Documento: <strong>{selectedDocument?.nome_arquivo}</strong>
              </Typography>
              <Typography variant="caption">
                Você está prestes a assinar este documento digitalmente. A assinatura será adicionada ao PDF.
              </Typography>
            </Alert>
            
            <Tabs 
              value={assinaturaTipo} 
              onChange={(_, value) => setAssinaturaTipo(value)}
              sx={{ mb: 3 }}
            >
              <Tab label="Assinatura Digital" value="digital" icon={<EditIcon />} />
              <Tab label="Upload de Assinatura" value="upload" icon={<UploadIcon />} />
            </Tabs>
            
            <Grid container spacing={3}>
              {assinaturaTipo === 'digital' ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="subtitle1" gutterBottom color="primary.main">
                      <EditIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Criar Assinatura Digital
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Digite seu nome completo para criar uma assinatura digital personalizada
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Digite sua assinatura"
                      value={manualSignature}
                      onChange={handleManualSignatureChange}
                      placeholder="Ex: João da Silva"
                      sx={{ mb: 2 }}
                      helperText="Use seu nome completo como assinatura"
                    />
                    
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleCreateSignatureFromText}
                      disabled={!manualSignature.trim()}
                      sx={{ py: 1.5 }}
                    >
                      Criar Assinatura Digital
                    </Button>
                  </Paper>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="subtitle1" gutterBottom color="primary.main">
                      <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Upload da Assinatura
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Faça upload de uma imagem da sua assinatura (PNG ou JPG, máximo 5MB)
                    </Typography>
                    
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="signature-upload"
                      type="file"
                      onChange={handleSignatureUpload}
                    />
                    
                    {!signatureImage ? (
                      <Box 
                        sx={{ 
                          border: '2px dashed',
                          borderColor: 'grey.400',
                          borderRadius: 2,
                          p: 4,
                          textAlign: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.light',
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => document.getElementById('signature-upload')?.click()}
                      >
                        <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Clique para selecionar uma imagem da sua assinatura
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ou arraste e solte o arquivo aqui
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Assinatura selecionada:
                        </Typography>
                        <SignaturePreviewBox src={signatureImage} />
                        <Button
                          startIcon={<ClearIcon />}
                          onClick={handleRemoveSignature}
                          color="error"
                          size="small"
                          variant="outlined"
                        >
                          Remover Assinatura
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
              
              {signatureImage && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, mt: 2, borderRadius: 2, bgcolor: 'success.light' }}>
                    <Typography variant="subtitle1" gutterBottom color="success.main">
                      Sua Assinatura Está Pronta
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <SignaturePreviewBox src={signatureImage} />
                      <Typography variant="body2" color="text.secondary">
                        Esta será a assinatura aplicada ao documento
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button 
              onClick={handleCloseSignModal} 
              color="inherit"
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSignDocument}
              disabled={!signatureImage || isSigning}
              startIcon={isSigning ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{ minWidth: 200, py: 1.5 }}
            >
              {isSigning ? 'Assinando...' : 'Confirmar e Assinar Documento'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Assinatura Concluída */}
        <Dialog 
          open={assinaturaConcluida} 
          onClose={() => setAssinaturaConcluida(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Documento Assinado com Sucesso!
            </Typography>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Uma cópia do documento assinado foi enviada para seu email.
            </Alert>
            
            <Typography variant="body2" paragraph color="text.secondary">
              Seu documento foi assinado digitalmente e uma cópia foi enviada para {servidorData?.email}.
              Você pode baixar o documento assinado agora ou acessá-lo posteriormente.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  handleDownloadSignedPdf();
                  setAssinaturaConcluida(false);
                }}
              >
                Baixar Documento Assinado
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setAssinaturaConcluida(false);
                  handleRefresh();
                }}
              >
                Continuar
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default DocumentPage;